import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../core/context/AuthContext";
import { initiateGoogleAuth } from "../../core/auth/google/googleAuth";
import LoggedInCTAButtons from "../../core/components/LoggedInCTAButtons";
import { SuccessButton, SecondaryButton, Container } from "../../design-system";
import { GoogleIcon, ArrowRightIcon } from "../../design-system/icons";
import { typography, surfaces, textColors, brandColors } from "../../design-system/tokens";
import WaveBackground from "./WaveBackground";
import { buildReferralSearchParams, setReferralCoupon } from "../../core/utils/referralCoupon";
import { submitPotentialCustomerLead } from "../../core/utils/leadsApi";

const FinalCTA = ({ referral = null, isFreeGrantActive = false } = {}) => {
  const [email, setEmail] = useState("");
  const { auth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const referralSearch = referral ? buildReferralSearchParams(referral) : "";
  const landingLeadSituation = "LANDING_EMAIL";

  const handleCreateWebsite = () => {
    const trimmedEmail = (email || "").trim();
    const isValid = trimmedEmail.length > 0 && trimmedEmail.includes("@");
    if (isValid) {
      // Capture as a PotentialCustomerLead (non-blocking, survives navigation).
      void submitPotentialCustomerLead(
        { email: trimmedEmail, situation: landingLeadSituation },
        { keepalive: true }
      ).catch((err) => {
        // Never block the CTA on lead capture failure.
        console.error("Failed to submit potential customer lead:", err);
      });

      // Redirect to register page with email pre-filled
      const base = `/edit/register?email=${encodeURIComponent(trimmedEmail)}`;
      const nextUrl = `${base}${referralSearch ? `&${referralSearch.replace(/^\?/, "")}` : ""}`;
      navigate(nextUrl);
    }
  };

  const handleSocialLogin = () => {
    // Use the proper Google OAuth flow for registration
    if (referral?.code) setReferralCoupon(referral);
    initiateGoogleAuth('register');
  };

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  return (
    <div className={`relative ${surfaces.primary}`}>
      <WaveBackground variant="inverse" className="absolute inset-0 pointer-events-none" />
      <Container size="xl" className="relative py-8">
        <div className={`${surfaces.primary} rounded-lg shadow-md p-8`}>
          <h2 className={`${typography.h2} ${textColors.brightest} mb-8 text-center`}>
            {isFreeGrantActive ? (
              <>
                Ready to create your <span className="text-green-400 font-extrabold">free</span> professional website?
              </>
            ) : (
              <>
                Ready to create your <span className="text-green-400 font-extrabold">professional website</span>?
              </>
            )}
          </h2>
          
          <div className="max-w-md mx-auto">
            {!isHydrated ? null : auth && auth.userId ? (
              <LoggedInCTAButtons username={auth.username} />
            ) : (
              <>
                {/* Email input with arrow button */}
                <div className={`flex items-center ${surfaces.primary} rounded-lg shadow-md p-2 mb-6`}>
                  <input
                    type="email"
                    aria-label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className={`flex-1 px-4 py-3 ${textColors.primary} ${surfaces.primary} focus:outline-none text-base`}
                  />
                  <SuccessButton
                    onClick={handleCreateWebsite}
                    disabled={!isEmailValid}
                    aria-label="Get started"
                    rightIcon={<ArrowRightIcon />}
                    className="p-3"
                  >
                  </SuccessButton>
                </div>

                {/* "or" divider */}
                <div className="mb-6">
                  <div className="flex items-center">
                    <div className={`flex-1 border-t ${brandColors.borderColor}`}></div>
                    <span className={`px-4 ${typography.muted} ${textColors.secondary}`}>or</span>
                    <div className={`flex-1 border-t ${brandColors.borderColor}`}></div>
                  </div>
                </div>

                {/* Google sign-in */}
                <SecondaryButton
                  type="button"
                  onClick={handleSocialLogin}
                  fullWidth={true}
                  leftIcon={<GoogleIcon size="sm" />}
                  className="rounded-lg"
                >
                  Sign up with Google
                </SecondaryButton>
              </>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default FinalCTA;